/**
 * Command Palette Search Service
 * 
 * Aggregates and searches across all content types in the database:
 * - Games
 * - Documents
 * - Glossary Items
 * - Dialogs
 * - UI Elements
 * - Folders
 */

import { objectsStore, ObjectItem } from '@/lib/stores/objectsStore';
import { documentStore, DocumentItem } from '@/lib/stores/documentStore';
import { dialogStore, Dialog } from '@/lib/stores/dialogStore';
import { uiElementsStore } from '@/lib/stores/uiElementsStore';
import { getFolders, getFolderName } from '@/lib/stores/folderStore';
import { Game } from '@/lib/stores/storeTypes';

export interface SearchResult {
  id: string;
  type: 'game' | 'document' | 'glossary' | 'dialog' | 'ui-element' | 'folder';
  title: string;
  subtitle?: string; // folder path, game name, etc.
  icon: string;
  gameId?: string;
  folderId?: string;
  path: string; // Route path to navigate to
  tags?: string[];
  score: number; // Relevance score for sorting
}

export interface GroupedSearchResults {
  games: SearchResult[];
  documents: SearchResult[];
  glossary: SearchResult[];
  dialogs: SearchResult[];
  uiElements: SearchResult[];
  folders: SearchResult[];
}

/**
 * Calculate relevance score for search matching
 * Exact match = 100, starts with = 75, contains = 50
 */
function calculateScore(searchTerm: string, text: string): number {
  const lowerSearch = searchTerm.toLowerCase();
  const lowerText = text.toLowerCase();
  
  if (lowerText === lowerSearch) return 100;
  if (lowerText.startsWith(lowerSearch)) return 75;
  if (lowerText.includes(lowerSearch)) return 50;
  return 0;
}

/**
 * Check if text matches search term
 */
function matchesSearch(searchTerm: string, text?: string | null): boolean {
  if (!text) return false;
  return text.toLowerCase().includes(searchTerm.toLowerCase());
}

/**
 * Search games
 */
function searchGames(searchTerm: string, games: Game[]): SearchResult[] {
  if (!searchTerm) return [];
  
  return games
    .filter(game => matchesSearch(searchTerm, game.name))
    .map(game => ({
      id: game.id,
      type: 'game' as const,
      title: game.name,
      subtitle: `Game`,
      icon: 'Gamepad2',
      gameId: game.id,
      path: `/home?game=${game.id}`,
      score: calculateScore(searchTerm, game.name),
    }))
    .sort((a, b) => b.score - a.score);
}

/**
 * Search documents
 */
function searchDocuments(searchTerm: string, currentGameId?: string): SearchResult[] {
  if (!searchTerm) return [];
  
  const allDocuments = documentStore.getData();
  const documents = currentGameId 
    ? allDocuments.filter(doc => doc.game_id === currentGameId)
    : allDocuments;
  
  return documents
    .filter(doc => 
      matchesSearch(searchTerm, doc.name) ||
      doc.tags?.some(tag => matchesSearch(searchTerm, tag))
    )
    .map(doc => {
      let subtitle = 'Document';
      if (doc.folder) {
        const folderName = getFolderName(doc.game_id, 'documents', doc.folder);
        if (folderName) subtitle = `Document • ${folderName}`;
      }
      
      return {
        id: doc.id,
        type: 'document' as const,
        title: doc.name,
        subtitle,
        icon: 'FileText',
        gameId: doc.game_id,
        folderId: doc.folder || undefined,
        path: `/documents/${doc.id}`,
        tags: doc.tags,
        score: calculateScore(searchTerm, doc.name),
      };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 10); // Limit to top 10 results per category
}

/**
 * Search glossary items
 */
function searchGlossaryItems(searchTerm: string, currentGameId?: string): SearchResult[] {
  if (!searchTerm) return [];
  
  const allItems = objectsStore.getData();
  const items = currentGameId 
    ? allItems.filter(item => item.game_id === currentGameId)
    : allItems;
  
  return items
    .filter(item => 
      matchesSearch(searchTerm, item.name) ||
      item.tags?.some(tag => matchesSearch(searchTerm, tag)) ||
      item.identifiers?.some(id => matchesSearch(searchTerm, id))
    )
    .map(item => {
      let subtitle = 'Glossary Item';
      if (item.folder) {
        const folderName = getFolderName(item.game_id, 'library', item.folder);
        if (folderName) subtitle = `Glossary • ${folderName}`;
      }
      
      return {
        id: item.id,
        type: 'glossary' as const,
        title: item.name,
        subtitle,
        icon: 'Library',
        gameId: item.game_id,
        folderId: item.folder || undefined,
        path: `/library?item=${item.id}`,
        tags: item.tags,
        score: Math.max(
          calculateScore(searchTerm, item.name),
          ...((item.identifiers || []).map(id => calculateScore(searchTerm, id)))
        ),
      };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);
}

/**
 * Search dialogs
 */
function searchDialogs(searchTerm: string, currentGameId?: string): SearchResult[] {
  if (!searchTerm) return [];
  
  const allDialogs = dialogStore.getData();
  const dialogs = currentGameId 
    ? allDialogs.filter(dialog => dialog.game_id === currentGameId)
    : allDialogs;
  
  return dialogs
    .filter(dialog => 
      matchesSearch(searchTerm, dialog.name) ||
      matchesSearch(searchTerm, dialog.dialog_key) ||
      dialog.tags?.some(tag => matchesSearch(searchTerm, tag))
    )
    .map(dialog => {
      let subtitle = 'Dialog';
      if (dialog.folder) {
        const folderName = getFolderName(dialog.game_id, 'documents', dialog.folder);
        if (folderName) subtitle = `Dialog • ${folderName}`;
      }
      
      return {
        id: dialog.id,
        type: 'dialog' as const,
        title: dialog.name,
        subtitle,
        icon: 'MessageSquare',
        gameId: dialog.game_id,
        folderId: dialog.folder || undefined,
        path: `/dialogs/${dialog.id}`,
        tags: dialog.tags,
        score: Math.max(
          calculateScore(searchTerm, dialog.name),
          calculateScore(searchTerm, dialog.dialog_key || '')
        ),
      };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);
}

/**
 * Search UI elements
 */
function searchUIElements(searchTerm: string, currentGameId?: string): SearchResult[] {
  if (!searchTerm) return [];
  
  const allElements = uiElementsStore.getData();
  const elements = currentGameId 
    ? allElements.filter((elem: any) => elem.game_id === currentGameId)
    : allElements;
  
  return elements
    .filter((elem: any) => 
      matchesSearch(searchTerm, elem.name) ||
      elem.tags?.some((tag: string) => matchesSearch(searchTerm, tag))
    )
    .map((elem: any) => ({
      id: elem.id,
      type: 'ui-element' as const,
      title: elem.name,
      subtitle: 'UI Element',
      icon: 'Blocks',
      gameId: elem.game_id,
      path: `/ui?item=${elem.id}`,
      tags: elem.tags,
      score: calculateScore(searchTerm, elem.name),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);
}

/**
 * Search folders across all types
 */
function searchFolders(searchTerm: string, currentGameId?: string): SearchResult[] {
  if (!searchTerm || !currentGameId) return [];
  
  const results: SearchResult[] = [];
  const folderTypes: Array<{ type: 'documents' | 'templates' | 'library', label: string, path: string }> = [
    { type: 'documents', label: 'Documents', path: '/documents' },
    { type: 'templates', label: 'Templates', path: '/templates' },
    { type: 'library', label: 'Library', path: '/library' },
  ];
  
  for (const { type, label, path } of folderTypes) {
    const folders = getFolders(currentGameId, type);
    
    const searchInFolders = (folderList: any[], parentPath = ''): void => {
      for (const folder of folderList) {
        if (matchesSearch(searchTerm, folder.name)) {
          const fullPath = parentPath ? `${parentPath} / ${folder.name}` : folder.name;
          results.push({
            id: folder.id,
            type: 'folder',
            title: folder.name,
            subtitle: `${label} Folder${parentPath ? ` • ${parentPath}` : ''}`,
            icon: 'Folder',
            gameId: currentGameId,
            folderId: folder.id,
            path: `${path}?folder=${folder.id}`,
            score: calculateScore(searchTerm, folder.name),
          });
        }
        
        if (folder.children && folder.children.length > 0) {
          const newPath = parentPath ? `${parentPath} / ${folder.name}` : folder.name;
          searchInFolders(folder.children, newPath);
        }
      }
    };
    
    searchInFolders(folders);
  }
  
  return results.sort((a, b) => b.score - a.score).slice(0, 10);
}

/**
 * Main search function that aggregates results from all sources
 */
export function searchAll(searchTerm: string, currentGameId?: string, games?: Game[]): GroupedSearchResults {
  const trimmedSearch = searchTerm.trim();
  
  if (!trimmedSearch) {
    return {
      games: [],
      documents: [],
      glossary: [],
      dialogs: [],
      uiElements: [],
      folders: [],
    };
  }
  
  return {
    games: games ? searchGames(trimmedSearch, games) : [],
    documents: searchDocuments(trimmedSearch, currentGameId),
    glossary: searchGlossaryItems(trimmedSearch, currentGameId),
    dialogs: searchDialogs(trimmedSearch, currentGameId),
    uiElements: searchUIElements(trimmedSearch, currentGameId),
    folders: searchFolders(trimmedSearch, currentGameId),
  };
}

/**
 * Get total count of results
 */
export function getResultCount(results: GroupedSearchResults): number {
  return (
    results.games.length +
    results.documents.length +
    results.glossary.length +
    results.dialogs.length +
    results.uiElements.length +
    results.folders.length
  );
}

/**
 * Flatten grouped results into a single array for keyboard navigation
 */
export function flattenResults(results: GroupedSearchResults): SearchResult[] {
  return [
    ...results.games,
    ...results.documents,
    ...results.glossary,
    ...results.dialogs,
    ...results.uiElements,
    ...results.folders,
  ];
}

